// Estimate enclosed mesh volume (mm³) + material weight from a binary glTF (.glb),
// mirroring the POC stepcheck.html `computeMetricsFromGroup`: a signed-tetrahedron
// sum over every triangle (bbox-centred for float stability), the same watertight
// guard, and the same steel/aluminium densities. Pure ArrayBuffer math — no three.js
// or model-viewer internals — so it runs on the same GLB bytes the viewer fetches
// from the presigned URL. Our exporter (svc-09 build_glb) writes identity-transform
// nodes with tightly-packed float VEC3 positions, so node transforms are ignored.

const GLB_MAGIC = 0x46546c67; // "glTF"
const JSON_CHUNK = 0x4e4f534a; // "JSON"

const CM3_PER_MM3 = 0.001;
const DENSITY_STEEL_G_CM3 = 7.85;
const DENSITY_AL_G_CM3 = 2.7;
const LB_PER_KG = 2.2046226218;

const COMP_FLOAT = 5126;
const COMP_UBYTE = 5121;
const COMP_USHORT = 5123;
const COMP_UINT = 5125;

export interface PartWeights {
  steelKg: number;
  steelLb: number;
  alKg: number;
  alLb: number;
}

interface GltfAccessor {
  bufferView?: number;
  byteOffset?: number;
  componentType: number;
  count: number;
  type: string;
  min?: number[];
  max?: number[];
}

interface GltfBufferView {
  byteOffset?: number;
  byteLength: number;
  byteStride?: number;
}

interface GltfDoc {
  meshes?: { primitives: { attributes: Record<string, number>; indices?: number }[] }[];
  accessors?: GltfAccessor[];
  bufferViews?: GltfBufferView[];
}

function indexReader(
  dv: DataView,
  binStart: number,
  view: GltfBufferView,
  acc: GltfAccessor,
): (i: number) => number {
  const base = binStart + (view.byteOffset ?? 0) + (acc.byteOffset ?? 0);
  switch (acc.componentType) {
    case COMP_UBYTE:
      return (i) => dv.getUint8(base + i);
    case COMP_USHORT:
      return (i) => dv.getUint16(base + i * 2, true);
    case COMP_UINT:
      return (i) => dv.getUint32(base + i * 4, true);
    default:
      throw new Error(`Unsupported index componentType ${acc.componentType}`);
  }
}

function positionReader(
  dv: DataView,
  binStart: number,
  view: GltfBufferView,
  acc: GltfAccessor,
): (v: number, out: Float64Array) => void {
  const base = binStart + (view.byteOffset ?? 0) + (acc.byteOffset ?? 0);
  const stride = view.byteStride && view.byteStride > 0 ? view.byteStride : 12;
  return (v, out) => {
    const o = base + v * stride;
    out[0] = dv.getFloat32(o, true);
    out[1] = dv.getFloat32(o + 4, true);
    out[2] = dv.getFloat32(o + 8, true);
  };
}

/**
 * Parse a .glb ArrayBuffer and return the estimated enclosed volume in mm³, or
 * `null` when the mesh is not closed enough to trust (matches the POC guard:
 * non-finite, ≤ 0, or < 1e-6 of the bounding-box volume).
 */
export function parseGlbVolumeMm3(buffer: ArrayBuffer): number | null {
  const dv = new DataView(buffer);
  if (dv.byteLength < 20 || dv.getUint32(0, true) !== GLB_MAGIC) return null;

  const jsonLen = dv.getUint32(12, true);
  if (dv.getUint32(16, true) !== JSON_CHUNK) return null;
  const jsonStart = 20;
  if (jsonStart + jsonLen + 8 > dv.byteLength) return null;

  let gltf: GltfDoc;
  try {
    const jsonText = new TextDecoder().decode(new Uint8Array(buffer, jsonStart, jsonLen));
    gltf = JSON.parse(jsonText) as GltfDoc;
  } catch {
    return null;
  }

  const binStart = jsonStart + jsonLen + 8; // skip BIN chunk [length][type]
  const accessors = gltf.accessors ?? [];
  const views = gltf.bufferViews ?? [];
  const meshes = gltf.meshes ?? [];
  if (meshes.length === 0) return null;

  // Bounding box over every POSITION accessor (uses accessor.min/max our exporter
  // writes; falls back to scanning vertices when absent).
  let minX = Infinity, minY = Infinity, minZ = Infinity;
  let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;
  const tmp = new Float64Array(3);

  type Prim = { posAcc: GltfAccessor; posView: GltfBufferView; idxAcc: GltfAccessor; idxView: GltfBufferView };
  const prims: Prim[] = [];

  for (const mesh of meshes) {
    for (const prim of mesh.primitives ?? []) {
      const posIdx = prim.attributes?.POSITION;
      if (posIdx == null || prim.indices == null) continue;
      const posAcc = accessors[posIdx];
      const idxAcc = accessors[prim.indices];
      if (!posAcc || !idxAcc || posAcc.componentType !== COMP_FLOAT) continue;
      const posView = views[posAcc.bufferView ?? -1];
      const idxView = views[idxAcc.bufferView ?? -1];
      if (!posView || !idxView) continue;
      prims.push({ posAcc, posView, idxAcc, idxView });

      if (posAcc.min && posAcc.max && posAcc.min.length >= 3 && posAcc.max.length >= 3) {
        minX = Math.min(minX, posAcc.min[0]); minY = Math.min(minY, posAcc.min[1]); minZ = Math.min(minZ, posAcc.min[2]);
        maxX = Math.max(maxX, posAcc.max[0]); maxY = Math.max(maxY, posAcc.max[1]); maxZ = Math.max(maxZ, posAcc.max[2]);
      } else {
        const read = positionReader(dv, binStart, posView, posAcc);
        for (let v = 0; v < posAcc.count; v++) {
          read(v, tmp);
          minX = Math.min(minX, tmp[0]); minY = Math.min(minY, tmp[1]); minZ = Math.min(minZ, tmp[2]);
          maxX = Math.max(maxX, tmp[0]); maxY = Math.max(maxY, tmp[1]); maxZ = Math.max(maxZ, tmp[2]);
        }
      }
    }
  }

  if (prims.length === 0 || !Number.isFinite(minX) || !Number.isFinite(maxX)) return null;

  const cx = (minX + maxX) / 2;
  const cy = (minY + maxY) / 2;
  const cz = (minZ + maxZ) / 2;
  const sizeX = maxX - minX;
  const sizeY = maxY - minY;
  const sizeZ = maxZ - minZ;
  const bboxVol = Math.max(0, sizeX * sizeY * sizeZ);

  const a = new Float64Array(3);
  const b = new Float64Array(3);
  const c = new Float64Array(3);
  let vol6 = 0;

  for (const { posAcc, posView, idxAcc, idxView } of prims) {
    const readPos = positionReader(dv, binStart, posView, posAcc);
    const readIdx = indexReader(dv, binStart, idxView, idxAcc);
    const n = idxAcc.count - (idxAcc.count % 3);
    for (let i = 0; i < n; i += 3) {
      readPos(readIdx(i), a);
      readPos(readIdx(i + 1), b);
      readPos(readIdx(i + 2), c);
      const ax = a[0] - cx, ay = a[1] - cy, az = a[2] - cz;
      const bx = b[0] - cx, by = b[1] - cy, bz = b[2] - cz;
      const dx = c[0] - cx, dy = c[1] - cy, dz = c[2] - cz;
      const px = by * dz - bz * dy;
      const py = bz * dx - bx * dz;
      const pz = bx * dy - by * dx;
      vol6 += ax * px + ay * py + az * pz;
    }
  }

  const volumeMm3 = Math.abs(vol6) / 6;
  if (!Number.isFinite(volumeMm3) || volumeMm3 <= 0) return null;
  if (bboxVol > 0 && volumeMm3 < bboxVol * 1e-6) return null;
  return volumeMm3;
}

/** Steel + aluminium estimates from volume (POC densities). `null` when no volume. */
export function estimateWeights(volumeMm3: number | null): PartWeights | null {
  if (volumeMm3 == null || !Number.isFinite(volumeMm3) || volumeMm3 <= 0) return null;
  const cm3 = volumeMm3 * CM3_PER_MM3;
  const steelKg = (cm3 * DENSITY_STEEL_G_CM3) / 1000;
  const alKg = (cm3 * DENSITY_AL_G_CM3) / 1000;
  return {
    steelKg,
    steelLb: steelKg * LB_PER_KG,
    alKg,
    alLb: alKg * LB_PER_KG,
  };
}
